'use client';

import Link from '@eliadmin/core/src/components/link';

import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { TDynamicData } from '../../types/dynamic-data';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Field } from '../form/field';
import { Paragraph } from '../ui/paragraph';

const contactSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    custom: z.object({
      interest: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    }),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    phone: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    text: z.string(),
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    'g-recaptcha-response': z.string().default(''),
  });

export type ContactFormValues = z.infer<ReturnType<typeof contactSchema>>;

export function ContactForm({ interests }: { interests?: TDynamicData[] }) {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      custom: {
        interest: '',
      },
      email: '',
      phone: '',
      text: '',
      gdpr: true,
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: sendContactMessage,
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {

      await mutateAsync({
        data: {
          ...data,

        },
        locale,
      });
      toast({
        title: t('text658'),
        variant: 'success',
      });
      router.push(ROUTES.THANK_YOU());
    } catch (err) {
      // handle 422
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text657'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className={cn(
          'flex w-full flex-col items-start justify-start gap-6 sm:gap-8',
          'ea-contact-form',
        )}
      >
        <div className={cn('grid w-full grid-cols-12 gap-4', 'ea-contact-form__fields-wrapper')}>
          <Field
            required
            control={form.control}
            name="name"
            ariaLabel={t('text3179')}
            placeholder={`${t('text3179')} *`}
            className={{ wrapper: 'col-span-12 w-full' }}
          />
          <Field
            required
            control={form.control}
            name="phone"
            ariaLabel={t('text1258')}
            placeholder={`${t('text1258')} *`}
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
          />
          <Field
            required
            control={form.control}
            type="email"
            name="email"
            ariaLabel="E-mail"
            placeholder="E-mail *"
            className={{ wrapper: 'col-span-12 w-full bg-[#F9FAFB] sm:col-span-6 rounded-[6px]' }}
          />
          <Field
            required
            control={form.control}
            type="select"
            name="custom.interest"
            placeholder={`${t('text2339')} *`}
            ariaLabel={t('text2339')}
            className={{ wrapper: 'col-span-12 w-full' }}
            options={interests?.map(interest => ({
              label: (interest?.name as string) || '',
              value: interest.id,
            }))}
          />
          <FormField
            control={form.control}
            name={'text'}
            render={({ field }) => (
              <FormItem
                required
                className={cn(
                  'col-span-12 w-full',
                  'ea-contact-form__field ea-contact-form__text-field',
                )}
              >
                <FormControl>
                  <Textarea
                    aria-label={t('text2340')}
                    placeholder={t('text2340') + ' *'}
                    {...(field as any)}
                    className="min-h-[112px] rounded-[12px] bg-[#f9fafb] px-6 py-4 text-sm placeholder:text-[#9E9E9E] lg:text-base shadow-none"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              <FormItem required className={cn('col-span-12 hidden')}>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="col-span-12 gap-6 sm:gap-8 md:gap-10 flex flex-col justify-start sm:flex-row w-full items-start sm:justify-between xl:gap-12">
          <Paragraph className="text-black">
            {t('text2341')}&nbsp;
            <Link
              prefetch={false}
              href={ROUTES.GDPR()}
              className="text-primary underline transition-all hover:opacity-80"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('text2342')}
            </Link>
          </Paragraph>
          <Button
            type="submit"
            aria-label={t('text185')}
            className={cn('whitespace-nowrap w-full sm:w-fit', 'ea-contact-form__submit')}
            isLoading={form.formState.isSubmitting}
          >
            {t('text185')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
